import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Car, TrendingUp, Shield, Zap, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate("/dashboard", { replace: true });
      } catch {
        setChecking(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="glass-header fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
              <Car className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AutoEstim
            </span>
          </div>
          <Button
            onClick={handleGoogleLogin}
            className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-6 py-2 btn-press"
            data-testid="header-login-button"
          >
            Connexion
          </Button>
        </div>
      </header>

      {/* Hero Section - Swiss Asymmetric Layout */}
      <section className="pt-32 pb-16 md:pb-24 md:pt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <div className="md:col-span-6 lg:col-span-5 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 text-sm font-medium mb-6">
                <Zap className="w-3.5 h-3.5" />
                Estimation par IA
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Estimez le prix de votre
                <span className="text-orange-500"> voiture</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-lg">
                Obtenez une estimation precise de votre vehicule basee sur les donnees du 
                Bon Coin, La Centrale et l'Argus. Analyse par intelligence artificielle.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleGoogleLogin}
                  className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium px-8 py-3 h-auto text-base btn-press flex items-center gap-2"
                  data-testid="hero-login-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuer avec Google
                </Button>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="md:col-span-6 lg:col-span-7 animate-fade-in-up delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1570374910698-6db3d787e6fb?auto=format&fit=crop&q=80&w=1200"
                  alt="Voiture de luxe"
                  className="w-full h-[300px] md:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 backdrop-blur-md rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Estimation moyenne</p>
                      <p className="text-xs text-slate-500">Basee sur 3 sources du marche francais</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>15 400 EUR</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Comment ca marche
            </h2>
            <p className="mt-3 text-base md:text-lg text-slate-600">Trois etapes simples pour connaitre la valeur de votre vehicule</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Car,
                title: "Decrivez votre vehicule",
                desc: "Renseignez la marque, le modele, l'annee, le kilometrage et l'etat de votre voiture.",
                step: "01"
              },
              {
                icon: Shield,
                title: "Ajoutez des photos",
                desc: "Uploadez jusqu'a 5 photos pour une analyse IA plus precise de l'etat de votre vehicule.",
                step: "02"
              },
              {
                icon: TrendingUp,
                title: "Recevez votre estimation",
                desc: "Obtenez les prix estimes sur Le Bon Coin, La Centrale et l'Argus en quelques secondes.",
                step: "03"
              }
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-100 p-8 card-hover animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}
                data-testid={`feature-card-${i}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl font-bold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.step}
                  </span>
                  <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pret a estimer votre vehicule ?
          </h2>
          <p className="text-base md:text-lg text-slate-600 mb-8">
            Rejoignez des milliers d'utilisateurs qui font confiance a AutoEstim pour estimer la valeur de leur voiture.
          </p>
          <Button
            onClick={handleGoogleLogin}
            className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium px-8 py-3 h-auto text-base btn-press"
            data-testid="cta-login-button"
          >
            Commencer gratuitement
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center">
              <Car className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>AutoEstim</span>
          </div>
          <p className="text-xs text-slate-400">Estimation basee sur les donnees du marche francais</p>
        </div>
      </footer>
    </div>
  );
}
